/**
 * TCL Outlet carousel — swipeable outlet-photo strip for `sections/tcl-boutiques.liquid`.
 *
 * WHY A NATIVE SCROLL CONTAINER, NOT A GSAP DRAGGABLE TRANSFORM TRACK
 * -------------------------------------------------------------------
 * The reference CodePen scrubs a paused GSAP timeline with Draggable +
 * ThrowProps. `tcl-logo-slider` (PR #15) went transform-based because CSS
 * scroll-snap ALONE gives no desktop mouse click-drag. That objection is real,
 * but it is an objection to scroll-snap *alone* — a pointerdown -> scrollLeft
 * layer (below) supplies exactly the missing piece. Keeping the native scroller
 * then buys, for free and correct-by-construction: keyboard scrolling,
 * focus-brings-into-view, correct screen-reader order, native iOS rubber-band
 * momentum, and wheel/trackpad. Those weigh more than 1:1 reference fidelity
 * here, because the section ships with a single photo today and the strip must
 * stay complete and operable with no JS at all.
 *
 * Consequence: no `Draggable.min.js` / `InertiaPlugin.min.js` tags, and no gsap
 * usage at all (gsap is loaded once theme-wide in layout/theme.liquid and must
 * never be re-tagged per section — PR #60). The release animation is a ~20-line
 * rAF tween below, which also means we own its completion signal (see next).
 *
 * SNAPPING OWNERSHIP
 * ------------------
 * `scroll-snap-type: x mandatory` stays ON for native touch/wheel scrolling.
 * For OUR animations (drag release, arrows, dots, arrow keys) snapping is
 * switched off inline first and restored in the tween's own onComplete. We own
 * the completion signal, so there is no `scrollend`/timeout race and the
 * browser can never re-snap and fight a tween mid-flight.
 *
 * PROGRESSIVE ENHANCEMENT
 * -----------------------
 * The markup ships as a working scroll-snap strip with every photo
 * server-rendered. This file adds `.tl-ocar--on`, which is the ONLY thing that
 * reveals the arrows/dots and enables the centre scale/brighten. With no JS the
 * strip is still swipeable, keyboard-scrollable and complete.
 * `prefers-reduced-motion: reduce` -> no scale/dim interpolation (CSS opts out
 * too) and instant jumps instead of tweens.
 *
 * INFINITE LOOP (opt-in: `data-oc-loop` on the root)
 * ----------------------------------------------------
 * A native scroller has ends, so looping is done with clones: one full copy of
 * the real slides is appended on each side (aria-hidden + inert, never
 * focusable or announced). Moving past the last real slide tweens forward
 * onto the leading clone of slide 1 — the visitor sees the strip keep moving
 * right — and only once the movement has finished is scrollLeft shifted by
 * exactly one set's width onto the real slide 1, which is pixel-identical, so
 * the jump is invisible. Our own movements re-home in the tween's completion;
 * native touch/wheel scrolling re-homes only after it settles (`scrollend`, or
 * an idle debounce), never mid-momentum or under a held finger. Dots, the live
 * status and `tcl-oc:change` always speak in REAL indices (mod n). Only
 * tcl-boutiques opts in: the how-it-works tabs ARE the dots and must not wrap.
 *
 * N=1 is handled in Liquid, not here: the section emits a plain <img> and does
 * not even load this file when there is a single photo.
 *
 * SHARED ENGINE
 * -------------
 * `sections/tcl-how-it-works.liquid` drives its synced-tab carousel from this
 * same file — its tabs are `[data-oc-dot]` elements, so click-to-slide and the
 * `aria-current` sync come for free and there is no third carousel in the
 * theme. Anything keying off this engine needs only the `data-oc-*` contract
 * and the `tcl-oc:change` event; the `--oc-t` centre-weight property is written
 * unconditionally and is simply ignored by sections that do not read it.
 *
 * Because two sections can now request this file on one page, and a classic
 * script with a repeated `src` RE-EXECUTES (the PR #60 GSAP bug), the whole IIFE
 * is guarded below. Without the guard a second execution would build a second
 * `instances` array and a second set of document-level `shopify:section:load`
 * listeners, and a teardown driven by one array could never reach the other
 * array's instances — leaking exactly the zombie listeners PR #60 fixed.
 */

(function () {
  'use strict';

  if (window.__tclOutletCarouselLoaded) return;
  window.__tclOutletCarouselLoaded = true;

  /* Queried per use, not latched at load: a visitor can turn "reduce motion" on
     mid-session (macOS and iOS both apply it live), and a value read once at
     script evaluation would keep animating for the rest of the page's life. */
  var REDUCE_MQ =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function reduceMotion() {
    return !!(REDUCE_MQ && REDUCE_MQ.matches);
  }

  /* Distance (px) the pointer must travel before a release counts as a drag
     rather than a click. Also the click-suppression threshold. */
  var DRAG_SLOP = 6;
  /* How many ms of coasting a flick is projected forward for velocity-aware
     snapping (the reference's ThrowProps behaviour, hand-rolled). */
  var COAST_MS = 140;
  /* Cap on how many slides a single flick may travel. */
  var MAX_FLICK = 3;
  var TWEEN_MS = 420;
  /* Quiet period after the last native scroll event before a loop re-home is
     allowed (fallback for browsers without `scrollend`). */
  var SETTLE_MS = 160;

  function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
  }

  function easeOutCubic(p) {
    return 1 - Math.pow(1 - p, 3);
  }

  function Carousel(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-oc-viewport]');
    this.track = root.querySelector('[data-oc-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-oc-slide]'));
    // Real slide count. In loop mode `this.slides` becomes every CELL in DOM
    // order (clones + real); `this.k` is the index of the first real slide.
    this.n = this.slides.length;
    this.k = 0;
    this.loop = root.hasAttribute('data-oc-loop') && this.n >= 2;
    this.clones = [];
    this.prevBtn = root.querySelector('[data-oc-prev]');
    this.nextBtn = root.querySelector('[data-oc-next]');
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-oc-dot]'));
    this.current = root.querySelector('[data-oc-current]');

    this.index = 0;
    this.raf = 0;
    this.tweenId = 0;
    this.dragging = false;
    this.moved = 0;
    this.bound = [];
  }

  Carousel.prototype.mount = function () {
    // Defensive: the Liquid never emits a carousel below two slides, but a
    // merchant deleting blocks in the editor can race the re-render.
    if (!this.viewport || !this.track || this.slides.length < 2) return false;

    var self = this;

    if (this.loop) this.buildClones();

    this.on(this.viewport, 'scroll', function () {
      self.schedule();
      if (self.loop) {
        clearTimeout(self.settleTimer);
        self.settleTimer = setTimeout(function () {
          self.settle();
        }, SETTLE_MS);
      }
    }, { passive: true });

    if (this.loop) {
      this.on(this.viewport, 'scrollend', function () {
        clearTimeout(self.settleTimer);
        self.settle();
      });
      // A finger resting on the strip produces no scroll events, which would
      // satisfy the idle debounce — so a held touch blocks re-homing outright.
      this.on(this.viewport, 'touchstart', function () {
        self.touching = true;
      }, { passive: true });
      var lift = function () {
        self.touching = false;
        clearTimeout(self.settleTimer);
        self.settleTimer = setTimeout(function () {
          self.settle();
        }, SETTLE_MS);
      };
      this.on(this.viewport, 'touchend', lift, { passive: true });
      this.on(this.viewport, 'touchcancel', lift, { passive: true });
    }

    this.on(this.viewport, 'keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.goTo(self.index + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.goTo(self.index - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        self.goTo(self.k);
      } else if (e.key === 'End') {
        e.preventDefault();
        self.goTo(self.k + self.n - 1);
      }
    });

    // Native image drag would hijack a mouse drag gesture.
    this.on(this.track, 'dragstart', function (e) {
      e.preventDefault();
    });

    this.on(this.viewport, 'pointerdown', function (e) {
      self.onDown(e);
    });
    this.on(this.viewport, 'pointermove', function (e) {
      self.onMove(e);
    });
    this.on(this.viewport, 'pointerup', function (e) {
      self.onUp(e);
    });
    this.on(this.viewport, 'pointercancel', function (e) {
      self.onUp(e);
    });

    // Suppress the synthetic click that follows a drag, so a future linked
    // caption can never be activated by the end of a swipe.
    this.on(this.viewport, 'click', function (e) {
      if (self.moved > DRAG_SLOP) {
        e.preventDefault();
        e.stopPropagation();
      }
      self.moved = 0;
    }, true);

    if (this.prevBtn) {
      this.on(this.prevBtn, 'click', function () {
        self.goTo(self.index - 1);
      });
    }
    if (this.nextBtn) {
      this.on(this.nextBtn, 'click', function () {
        self.goTo(self.index + 1);
      });
    }
    this.dots.forEach(function (dot, i) {
      self.on(dot, 'click', function (e) {
        /* The outlet carousel's dots are <button>s, where this is a no-op. The
           how-it-works tabs are <a href="#panel-id"> so that they still work
           with no JS — and there the browser's fragment jump would otherwise
           run ALONGSIDE the tween: `tweenTo` captures `from = scrollLeft`
           synchronously, the default action then slams scrollLeft to the target
           AND scrolls the document vertically, and the next rAF interpolates
           from the now-stale origin. Visible result is jump, snap back, slide
           forward, plus a history entry per tab click so Back walks the tabs
           instead of leaving the page. */
        if (e && e.cancelable) e.preventDefault();
        self.goToReal(i);
      });
    });

    if (window.ResizeObserver) {
      // Pitch is measured, never hardcoded — recompute when the box changes.
      this.ro = new ResizeObserver(function () {
        self.schedule();
      });
      this.ro.observe(this.viewport);
    }

    /* Force the first `setIndex` to do real work. It early-returns on
       `i === this.index`, and `this.index` is already 0 at construction — so
       the initial paint never applied the index state at all: Prev shipped
       WITHOUT `disabled`, looking active while `goTo(-1)` clamped and did
       nothing, and no dot got `aria-current`. Harmless for the outlet
       carousel's dots, but the how-it-works tabs ARE the dots, so the strip
       opened with no tab marked current. */
    this.index = -1;
    if (this.loop) {
      // Open on the first REAL slide, instantly, with snapping out of the way.
      this.viewport.style.scrollSnapType = 'none';
      this.viewport.scrollLeft = this.scrollFor(this.k);
      this.viewport.style.scrollSnapType = '';
    }
    this.paint();
    this.root.classList.add('tl-ocar--on');
    this.startAutoplay();
    return true;
  };

  /* ---- autoplay (opt-in: `data-oc-autoplay="<ms>"` on the root) ----------
   * Only tcl-boutiques sets the attribute; the how-it-works strip shares this
   * script and must never advance on its own.
   *
   * EVERY PAUSE CONDITION IS READ LIVE, never latched into a flag. The press
   * slider shipped a focus-within flag that a drag set and nothing cleared (the
   * browser's focus fixup moves focus to <body> without a `focusout`), so its
   * autoplay stopped for good. Here hover and focus are `matches()` at tick
   * time, the tab state is `document.hidden`, and reduced motion is the live
   * media query. The only stored inputs are "last interaction" (a timestamp
   * that ages out) and "in view" (an IntersectionObserver that reports both
   * edges). */
  Carousel.prototype.startAutoplay = function () {
    var ms = parseInt(this.root.getAttribute('data-oc-autoplay'), 10);
    if (!(ms >= 1500)) return;
    var self = this;
    this.autoMs = ms;
    this.lastInteract = 0;
    this.inView = true;
    var poke = function () {
      self.lastInteract = performance.now();
    };
    ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach(function (t) {
      self.on(self.root, t, poke, { passive: true });
    });
    if (window.IntersectionObserver) {
      this.io = new IntersectionObserver(function (entries) {
        self.inView = entries[entries.length - 1].isIntersecting;
      });
      this.io.observe(this.root);
    }
    var tick = function () {
      self.autoTimer = setTimeout(tick, self.autoMs);
      if (!self.canPlay()) return;
      if (self.loop) {
        self.goTo(self.index + 1);
      } else {
        self.goTo(self.index >= self.slides.length - 1 ? 0 : self.index + 1);
      }
    };
    this.autoTimer = setTimeout(tick, ms);
  };

  Carousel.prototype.canPlay = function () {
    if (document.hidden || reduceMotion() || this.dragging || !this.inView) return false;
    if (this.root.matches(':hover')) return false;
    // Keyboard focus only: a mouse click on an arrow leaves that button focused
    // indefinitely, and pausing on plain :focus-within would stop autoplay for
    // good after one click. A recent click is already covered by lastInteract.
    try {
      if (this.root.querySelector(':focus-visible')) return false;
    } catch (e) {
      if (this.root.matches(':focus-within')) return false;
    }
    if (performance.now() - this.lastInteract < this.autoMs) return false;
    return true;
  };

  Carousel.prototype.on = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    this.bound.push([el, type, fn, opts]);
  };

  Carousel.prototype.destroy = function () {
    this.bound.forEach(function (b) {
      b[0].removeEventListener(b[1], b[2], b[3]);
    });
    this.bound = [];
    if (this.ro) this.ro.disconnect();
    if (this.io) this.io.disconnect();
    if (this.autoTimer) clearTimeout(this.autoTimer);
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.tweenId++;
    this.clones.forEach(function (c) {
      if (c.parentNode) c.parentNode.removeChild(c);
    });
    this.clones = [];
    this.root.classList.remove('tl-ocar--on');
  };

  /* ---- loop ----------------------------------------------------------- */

  Carousel.prototype.buildClones = function () {
    var real = this.slides;
    var track = this.track;
    var self = this;
    function cloneOf(slide) {
      var c = slide.cloneNode(true);
      // Not a slide for anything that queries the DOM (a re-mount, or the
      // editor's block:select lookup, which would otherwise hit a clone first).
      c.removeAttribute('data-oc-slide');
      c.removeAttribute('data-oc-block-id');
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('inert', '');
      c.classList.add('tl-ocar__slide--clone');
      // A lazy clone can still be decoding when the wrap lands on it. Same URL
      // as its twin, so eager costs no extra bytes once the real one loaded.
      Array.prototype.forEach.call(c.querySelectorAll('img[loading="lazy"]'), function (img) {
        img.setAttribute('loading', 'eager');
      });
      self.clones.push(c);
      return c;
    }
    var first = real[0];
    real.forEach(function (slide) {
      track.insertBefore(cloneOf(slide), first);
    });
    real.forEach(function (slide) {
      track.appendChild(cloneOf(slide));
    });
    var n = this.n;
    this.slides = this.clones.slice(0, n).concat(real, this.clones.slice(n));
    this.k = n;
  };

  Carousel.prototype.realOf = function (i) {
    var n = this.n;
    return (((i - this.k) % n) + n) % n;
  };

  /* Jump from a clone cell to its real twin without anything visibly moving:
     the shift is exactly the distance between the two cells' centres, so
     whatever sub-pixel offset the strip is resting at is preserved. */
  Carousel.prototype.rehome = function () {
    if (!this.loop) return;
    var i = this.nearestTo(this.viewport.scrollLeft);
    if (i >= this.k && i < this.k + this.n) return;
    var twin = this.k + this.realOf(i);
    var vp = this.viewport;
    vp.style.scrollSnapType = 'none';
    vp.scrollLeft += this.centreOf(twin) - this.centreOf(i);
    vp.style.scrollSnapType = '';
    // Repaint in this same frame so `--oc-t` never shows the twin dimmed.
    this.paint();
  };

  Carousel.prototype.settle = function () {
    if (this.dragging || this.touching || this.tweening) return;
    this.rehome();
  };

  /* ---- geometry ------------------------------------------------------- */

  Carousel.prototype.centreOf = function (i) {
    var s = this.slides[i];
    return s.offsetLeft + s.offsetWidth / 2;
  };

  /* Measured pitch (slide box + gap), from two real slides — this is the fix
     for the reference's hardcoded `cellWidth = 500`. `offsetLeft` is a layout
     value, so the CSS `scale()` on slides does not perturb it. */
  Carousel.prototype.pitch = function () {
    var p = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    return p > 0 ? p : this.slides[0].offsetWidth || 1;
  };

  Carousel.prototype.maxScroll = function () {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  };

  Carousel.prototype.scrollFor = function (i) {
    return clamp(
      this.centreOf(i) - this.viewport.clientWidth / 2,
      0,
      this.maxScroll()
    );
  };

  Carousel.prototype.nearestTo = function (scrollLeft) {
    var centre = scrollLeft + this.viewport.clientWidth / 2;
    var best = 0;
    var bestD = Infinity;
    for (var i = 0; i < this.slides.length; i++) {
      var d = Math.abs(this.centreOf(i) - centre);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  };

  /* ---- painting ------------------------------------------------------- */

  Carousel.prototype.schedule = function () {
    var self = this;
    if (this.raf) return;
    this.raf = requestAnimationFrame(function () {
      self.raf = 0;
      self.paint();
    });
  };

  Carousel.prototype.paint = function () {
    var vp = this.viewport;
    var centre = vp.scrollLeft + vp.clientWidth / 2;

    if (!reduceMotion()) {
      // One value per slide; all styling reads from it, so the whole look
      // stays in CSS (see the --oc-t maths in the section stylesheet).
      var pitch = this.pitch();
      for (var i = 0; i < this.slides.length; i++) {
        var off = this.centreOf(i) - centre;
        var t = Math.max(0, 1 - Math.abs(off) / pitch);
        this.slides[i].style.setProperty('--oc-t', t.toFixed(3));
        // Shrink TOWARD the centred slide (anchor on the facing edge), so the
        // scale-down widens the peek instead of opening a gap. The flip at
        // centre is invisible: scale is 1 there.
        this.slides[i].style.setProperty('--oc-ox', off < 0 ? '100%' : '0%');
      }
    }

    this.setIndex(this.nearestTo(vp.scrollLeft));
  };

  Carousel.prototype.setIndex = function (i) {
    if (i === this.index) return;
    var prevReal = this.index < 0 ? -1 : this.realOf(this.index);
    this.index = i;
    var real = this.realOf(i);
    this.syncArrows();
    // A clone cell and its real twin are the same slide to the visitor: no
    // dot change, no status re-announcement, no change event on a re-home.
    if (real === prevReal) return;

    this.dots.forEach(function (dot, n) {
      if (n === real) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
    if (this.current) this.current.textContent = String(real + 1);

    this.root.dispatchEvent(
      new CustomEvent('tcl-oc:change', { bubbles: true, detail: { index: real } })
    );
  };

  /* Disabling the button the user is standing on drops focus to <body>, so a
     keyboard visitor arrowing to the last slide loses their place and has to
     tab in from the top of the document again. Hand focus to the viewport —
     which is itself focusable and arrow-key scrollable — before disabling. */
  Carousel.prototype.syncArrows = function () {
    var self = this;

    function setDisabled(btn, off) {
      if (!btn) return;
      if (off && document.activeElement === btn && self.viewport) {
        self.viewport.focus({ preventScroll: true });
      }
      btn.disabled = off;
    }

    if (this.loop) {
      setDisabled(this.prevBtn, false);
      setDisabled(this.nextBtn, false);
      return;
    }
    setDisabled(this.prevBtn, this.index <= 0);
    setDisabled(this.nextBtn, this.index >= this.slides.length - 1);
  };

  /* ---- movement ------------------------------------------------------- */

  /**
   * Animate scrollLeft ourselves. Snapping is suspended for the duration and
   * restored only on completion — mandatory snap would otherwise yank every
   * intermediate frame back to a snap point and destroy the tween.
   */
  Carousel.prototype.tweenTo = function (to) {
    var vp = this.viewport;
    var from = vp.scrollLeft;
    var id = ++this.tweenId;

    var self = this;

    if (reduceMotion() || Math.abs(to - from) < 1) {
      vp.scrollLeft = to;
      this.tweening = false;
      this.rehome();
      return;
    }

    vp.style.scrollSnapType = 'none';
    var start = performance.now();
    this.tweening = true;

    function step(now) {
      if (id !== self.tweenId) return; // superseded (new gesture / destroyed)
      var p = clamp((now - start) / TWEEN_MS, 0, 1);
      vp.scrollLeft = from + (to - from) * easeOutCubic(p);
      if (p < 1) {
        requestAnimationFrame(step);
      } else {
        vp.style.scrollSnapType = '';
        self.tweening = false;
        self.rehome();
      }
    }
    requestAnimationFrame(step);
  };

  Carousel.prototype.goTo = function (i) {
    if (this.loop && (i < 0 || i > this.slides.length - 1)) {
      // Clicked faster than the tweens finish and ran off the clones: re-home
      // the strip where it stands (one set over — invisible), then retarget.
      this.tweenId++;
      this.tweening = false;
      var shift = i < 0 ? this.n : -this.n;
      var cur = this.nearestTo(this.viewport.scrollLeft);
      var vp = this.viewport;
      vp.style.scrollSnapType = 'none';
      vp.scrollLeft += this.centreOf(cur + shift) - this.centreOf(cur);
      i += shift;
      this.index += shift;
    }
    i = clamp(i, 0, this.slides.length - 1);
    this.tweenTo(this.scrollFor(i));
    this.setIndex(i);
  };

  /* Dots/tabs speak in real indices. In loop mode take the nearest copy of the
     target slide, so the strip never sweeps through a whole set to get there. */
  Carousel.prototype.goToReal = function (r) {
    if (!this.loop) return this.goTo(r);
    var cur = this.index < 0 ? this.k : this.index;
    var best = this.k + r;
    for (var c = r; c < this.slides.length; c += this.n) {
      if (Math.abs(c - cur) < Math.abs(best - cur)) best = c;
    }
    this.goTo(best);
  };

  Carousel.prototype.onDown = function (e) {
    // Touch already has native, platform-correct momentum + snapping. Only
    // mouse and pen need a hand-rolled drag.
    if (e.pointerType === 'touch') return;
    if (e.button !== 0) return;

    this.tweenId++; // cancel any in-flight tween
    this.tweening = false;
    this.dragging = true;
    this.moved = 0;
    this.startX = e.clientX;
    this.lastX = e.clientX;
    this.lastT = performance.now();
    this.velocity = 0;
    this.startScroll = this.viewport.scrollLeft;

    this.viewport.style.scrollSnapType = 'none';
    this.root.classList.add('tl-ocar--dragging');
    if (this.viewport.setPointerCapture) {
      try {
        this.viewport.setPointerCapture(e.pointerId);
      } catch (err) {
        /* capture is best-effort */
      }
    }
  };

  Carousel.prototype.onMove = function (e) {
    if (!this.dragging) return;
    var x = e.clientX;
    var now = performance.now();
    var dt = now - this.lastT;

    this.viewport.scrollLeft = this.startScroll - (x - this.startX);
    this.moved = Math.max(this.moved, Math.abs(x - this.startX));

    if (dt > 0) {
      // px/ms, smoothed. Positive = pointer moving right = scrollLeft falling.
      this.velocity = 0.8 * ((x - this.lastX) / dt) + 0.2 * this.velocity;
    }
    this.lastX = x;
    this.lastT = now;
  };

  Carousel.prototype.onUp = function (e) {
    if (!this.dragging) return;
    this.dragging = false;
    this.root.classList.remove('tl-ocar--dragging');
    if (this.viewport.releasePointerCapture && e.pointerId != null) {
      try {
        this.viewport.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* capture is best-effort */
      }
    }

    var from = this.nearestTo(this.viewport.scrollLeft);
    // Velocity-aware landing: project the flick forward, then snap that
    // projected resting point to the nearest slide (the reference's
    // ThrowProps + snap behaviour, without the plugin).
    var projected = this.viewport.scrollLeft - this.velocity * COAST_MS;
    var target = this.nearestTo(clamp(projected, 0, this.maxScroll()));
    target = clamp(target, from - MAX_FLICK, from + MAX_FLICK);

    this.goTo(target);
  };

  /* ---- wiring --------------------------------------------------------- */

  var instances = [];

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-tcl-oc]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.tclOcBound === 'true') return;
      var c = new Carousel(root);
      if (c.mount()) {
        root.dataset.tclOcBound = 'true';
        instances.push(c);
      }
    });
  }

  function teardown(scope) {
    for (var i = instances.length - 1; i >= 0; i--) {
      var c = instances[i];
      if (!scope || scope.contains(c.root)) {
        c.destroy();
        delete c.root.dataset.tclOcBound;
        instances.splice(i, 1);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: sections are re-rendered wholesale, so re-init and kill the
  // old instance or its listeners/RAF leak as zombies.
  document.addEventListener('shopify:section:load', function (e) {
    teardown(e.target);
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    teardown(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    // Only meaningful in `source: blocks` mode — in metaobject mode the slides
    // carry no block id and there is nothing to select.
    var blockId = e.detail && e.detail.blockId ? e.detail.blockId : e.target.id;
    if (!blockId) return;
    instances.forEach(function (c) {
      var slide = c.root.querySelector('[data-oc-block-id="' + blockId + '"]');
      if (!slide) return;
      var i = c.slides.indexOf(slide);
      if (i >= 0) c.goTo(i);
    });
  });
})();
